import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaBolt, FaPen, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import api from '../../services/api';
import { filterQuickReplies } from './quickReplies';

const EMPTY_FORM = { id: null, atalho: '', texto: '', escopo: 'pessoal' };

function QuickRepliesPanel({ replies, loadError, onInsert, onReload, canManageCompany, disabled }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const visible = useMemo(
    () => (search.trim() ? filterQuickReplies(replies, search) : replies),
    [replies, search],
  );

  const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

  const save = async () => {
    if (!form.atalho.trim() || !form.texto.trim()) {
      setError('Preencha o atalho e o texto.');
      return;
    }
    setSaving(true);
    try {
      const payload = { atalho: form.atalho, texto: form.texto };
      if (form.id) await api.put(`/mensagens-rapidas/${form.id}`, payload);
      else await api.post('/mensagens-rapidas', { ...payload, escopo: form.escopo });
      setForm(null);
      setError('');
      await onReload();
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível salvar a mensagem rápida.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Excluir a mensagem rápida "/${item.atalho}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/mensagens-rapidas/${item.id}`);
      setError('');
      await onReload();
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível excluir a mensagem rápida.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="zapi-whatsapp-quick-replies" ref={rootRef}>
      <button
        type="button"
        className="zapi-whatsapp-emoji-button"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        title="Mensagens rápidas (ou digite / na caixa de mensagem)"
        aria-label="Mensagens rápidas"
        aria-expanded={open}
      >
        <FaBolt />
      </button>
      {open && (
        <div className="zapi-whatsapp-quick-replies-panel" role="dialog" aria-label="Mensagens rápidas">
          <div className="zapi-whatsapp-quick-replies-header">
            <strong>Mensagens rápidas</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fechar"><FaTimes /></button>
          </div>
          {(error || loadError) && <div className="zapi-whatsapp-quick-replies-error">{error || loadError}</div>}

          {form ? (
            <div className="zapi-whatsapp-quick-replies-form">
              <label>
                Atalho
                <input
                  value={form.atalho}
                  maxLength={41}
                  onChange={(event) => setForm({ ...form, atalho: event.target.value })}
                  placeholder="ex.: orcamento"
                />
              </label>
              <label>
                Texto
                <textarea
                  value={form.texto}
                  maxLength={4096}
                  rows={4}
                  onChange={(event) => setForm({ ...form, texto: event.target.value })}
                  placeholder="Use {nome} para o primeiro nome do cliente"
                />
              </label>
              {!form.id && canManageCompany && (
                <label>
                  Quem pode usar
                  <select value={form.escopo} onChange={(event) => setForm({ ...form, escopo: event.target.value })}>
                    <option value="pessoal">Só eu</option>
                    <option value="empresa">Toda a empresa</option>
                  </select>
                </label>
              )}
              <div className="zapi-whatsapp-quick-replies-actions">
                <button type="button" onClick={() => { setForm(null); setError(''); }} disabled={saving}>Cancelar</button>
                <button type="button" className="is-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </div>
          ) : (
            <>
              <div className="zapi-whatsapp-quick-replies-toolbar">
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar atalho ou texto" />
                <button type="button" onClick={() => setForm({ ...EMPTY_FORM })} title="Nova mensagem rápida"><FaPlus /> Nova</button>
              </div>
              <div className="zapi-whatsapp-quick-replies-list">
                {visible.length === 0 && (
                  <p className="zapi-whatsapp-quick-replies-empty">
                    {replies.length ? 'Nada encontrado.' : 'Nenhuma mensagem rápida ainda. Clique em "Nova".'}
                  </p>
                )}
                {visible.map((item) => (
                  <div className="zapi-whatsapp-quick-replies-item" key={item.id}>
                    <button
                      type="button"
                      className="zapi-whatsapp-quick-replies-use"
                      onClick={() => { onInsert(item); setOpen(false); }}
                    >
                      <span>/{item.atalho} <em>{item.escopo === 'empresa' ? 'Empresa' : 'Minha'}</em></span>
                      <small>{item.texto}</small>
                    </button>
                    {item.pode_editar && (
                      <div className="zapi-whatsapp-quick-replies-item-actions">
                        <button type="button" onClick={() => setForm({ id: item.id, atalho: item.atalho, texto: item.texto, escopo: item.escopo })} aria-label={`Editar /${item.atalho}`} disabled={saving}><FaPen /></button>
                        <button type="button" onClick={() => remove(item)} aria-label={`Excluir /${item.atalho}`} disabled={saving}><FaTrash /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default QuickRepliesPanel;
